import { useState, useCallback } from 'react';
import { Notify } from 'notiflix';

import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import { Card, Grid, Icon, Divider } from '@mui/material';

import { ENTITY_TYPES, getEntityConfig, validateImportRow, parseImportFile } from './dataImportUtils';
import { dataImportApi } from './services/dataImportApi';
import ImportStepper from './ImportStepper';
import EntityTypeSelector from './EntityTypeSelector';
import FileUploadZone from './FileUploadZone';
import ImportPreviewTable from './ImportPreviewTable';

const MAX_PREVIEW_ROWS = 200;

const DataImport = () => {
  const [entityType, setEntityType] = useState(ENTITY_TYPES[0].value);
  const [fileName, setFileName] = useState(null);
  const [fileSize, setFileSize] = useState(null);
  const [rows, setRows] = useState([]); // [{ data, errors }]
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const entityConfig = getEntityConfig(entityType);
  const activeStep = rows.length > 0 ? 2 : fileName ? 1 : 0;

  const resetFile = () => {
    setFileName(null);
    setFileSize(null);
    setRows([]);
    setImportResult(null);
  };

  const handleEntityChange = (e) => {
    setEntityType(e.target.value);
    resetFile();
  };

  const handleDrop = useCallback(
    (acceptedFiles, fileRejections) => {
      if (fileRejections?.length > 0) {
        Notify.failure('Please upload a .xlsx, .xls, or .csv file');
        return;
      }
      const file = acceptedFiles[0];
      if (!file) return;

      setParsing(true);
      setImportResult(null);
      setFileName(file.name);
      setFileSize(file.size);

      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const parsedRows = parseImportFile(event.target.result);

          if (parsedRows.length === 0) {
            Notify.failure('The file has no data rows');
            setRows([]);
            setParsing(false);
            return;
          }

          const withValidation = parsedRows.slice(0, MAX_PREVIEW_ROWS).map((data) => ({
            data,
            errors: validateImportRow(data, entityConfig),
          }));
          setRows(withValidation);

          if (parsedRows.length > MAX_PREVIEW_ROWS) {
            Notify.info(`Showing the first ${MAX_PREVIEW_ROWS} of ${parsedRows.length} rows`);
          }
        } catch (err) {
          console.error('Failed to parse file:', err);
          Notify.failure('Could not read this file — make sure it’s a valid Excel or CSV file');
          setRows([]);
        } finally {
          setParsing(false);
        }
      };
      reader.onerror = () => {
        Notify.failure('Failed to read the file');
        setParsing(false);
      };
      reader.readAsArrayBuffer(file);
    },
    [entityConfig],
  );

  const handleImport = async () => {
    const validRows = rows.filter((r) => r.errors.length === 0);
    if (validRows.length === 0) return;

    setImporting(true);
    setImportResult(null);
    try {
      const res = await dataImportApi.importRows(entityType, validRows.map((r) => r.data));
      const summary = res?.data?.data || {};
      setImportResult({
        success: true,
        message: res?.data?.message || 'Import completed',
        created: summary.created ?? null,
        updated: summary.updated ?? null,
        skipped: summary.skipped ?? null,
        rowErrors: summary.rowErrors || [],
      });
      Notify.success('Import completed successfully');
    } catch (err) {
      setImportResult({
        success: false,
        message: err?.response?.data?.message || 'Import failed — please try again',
      });
      Notify.failure('Import failed');
    } finally {
      setImporting(false);
    }
  };

  const handleReset = () => {
    resetFile();
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={5} mb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card sx={{ p: 3 }}>
              <MDBox display="flex" alignItems="center" gap={1} mb={1}>
                <Icon fontSize="medium" color="info">upload_file</Icon>
                <MDTypography variant="h5" fontWeight="bold">
                  Bulk Data Import
                </MDTypography>
              </MDBox>
              <MDTypography variant="body2" color="text">
                Upload an Excel (.xlsx) or CSV file to bulk-create records instead of running seed
                scripts at the code level. Pick a data type, download its template if you need the
                exact column format, then upload your filled-in file to preview and validate it
                before importing.
              </MDTypography>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card sx={{ p: { xs: 2, md: 3 } }}>
              <ImportStepper activeStep={activeStep} />
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ p: 3 }}>
              <EntityTypeSelector
                entityType={entityType}
                onEntityChange={handleEntityChange}
                entityConfig={entityConfig}
              />

              <Divider sx={{ my: 2.5 }} />

              <FileUploadZone
                fileName={fileName}
                fileSize={fileSize}
                onDrop={handleDrop}
                onClearFile={resetFile}
              />
            </Card>
          </Grid>

          <Grid item xs={12} md={8}>
            <Card sx={{ p: 3, minHeight: 300 }}>
              <ImportPreviewTable
                parsing={parsing}
                rows={rows}
                entityConfig={entityConfig}
                importing={importing}
                importResult={importResult}
                onImport={handleImport}
                onReset={handleReset}
              />
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
};

export default DataImport;
